import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import ApartmentIcon from '@mui/icons-material/Apartment';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TuneIcon from '@mui/icons-material/Tune';
import Layout from '../../Component/layout';
import Integrations from './Integrations';
import PMS from './Pms';
import ClientInfo from './Pms/clientInfo';
import Settings from './Settings';
import Communities from './Communities';
import './styles.scss';
import FeatureFlags from './FeatureFlags';
import Client from './Client';
import { getTenantDetails, getClients } from '../../Redux/Actions/SelfServe';
import { resetTenantData } from '../../Redux/Reducer/SelfServe';
import logos from '../../Media/logos';
import { GlobalContext } from '../../utils/ContextProviders/GlobalContextProvider';

const SelfServe = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { userData } = useContext(GlobalContext);
  const [info, infoPopUPHandler] = useState(false);

  const [tenantId, setTenantId] = useState(
    () => sessionStorage.getItem('selfServe_tenantId') || ''
  );
  const [tenantName, setTenantName] = useState(
    () => sessionStorage.getItem('selfServe_tenantName') || ''
  );
  const selfServe = useSelector(state => state.SelfServe);
  const [notification, setNotification] = useState('');
  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem('selfServe_activeTab') || 'internalSettings'
  );
  const [searchVal, setSearchVal] = useState('');
  const [liveOnly, setLiveOnly] = useState(true);

  const integrationSettings = selfServe?.userData?.data?.integrationSettings;
  const crmSettings = selfServe?.userData?.data?.crmSettings;
  const restrictCommunication =
    selfServe?.userData?.data?.restrictCommunication;
  const isGPTEnabled = selfServe?.userData?.data?.isGPTEnable;
  const pmsName = integrationSettings && integrationSettings?.pms;
  const knockName = crmSettings?.crm;
  const isSuperAdmin = selfServe?.superAdmin;
  const isTenantLoaded = selfServe?.loadedTenantId === tenantId;

  useEffect(() => {
    sessionStorage.setItem('selfServe_tenantId', tenantId);
    sessionStorage.setItem('selfServe_tenantName', tenantName);
    sessionStorage.setItem('selfServe_activeTab', activeTab);
  }, [tenantId, tenantName, activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    dispatch(resetTenantData());
    setTenantId('');
    setTenantName('');
    setActiveTab('internalSettings');
  };

  useEffect(() => {
    if (tenantId) {
      dispatch(getTenantDetails(tenantId));
    }
  }, [tenantId]);

  useEffect(() => {
    dispatch(getClients());
  }, []);

  return isSuperAdmin === null ? null : (
    <Layout history={history}>
      {tenantId === '' ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              borderBottom: '1px solid',
              borderBottomColor: 'divider',
              py: 2
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Clients
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search clients"
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: 240,
                  minWidth: 240,
                  backgroundColor: 'background.paper',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    height: 40
                  }
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={liveOnly}
                    onChange={e => setLiveOnly(e.target.checked)}
                    size="small"
                  />
                }
                label="Live only"
                sx={{
                  ml: 0,
                  '& .MuiTypography-root': {
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'text.secondary'
                  }
                }}
              />
              {userData?.role?.name === 'SUPER_ADMIN' && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => infoPopUPHandler(true)}
                  sx={{ textTransform: 'none', height: 40 }}
                >
                  Add Client
                </Button>
              )}
            </Box>
          </Box>
          <Client
            searchVal={searchVal}
            liveOnly={liveOnly}
            setTenantId={setTenantId}
            setTenantName={setTenantName}
          />
        </>
      ) : (
        <Box key={tenantId}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              {tenantName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              {pmsName && logos[pmsName] && (
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 1.5,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <img
                    src={logos[pmsName]}
                    alt={pmsName}
                    style={{
                      maxWidth: 100,
                      maxHeight: 40,
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}
              {pmsName && !logos[pmsName] && (
                <Chip
                  label={`PMS - ${pmsName}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {knockName && logos[knockName] && (
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 1.5,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <img
                    src={logos[knockName]}
                    alt={knockName}
                    style={{
                      maxWidth: 100,
                      maxHeight: 40,
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}
              {knockName && !logos[knockName] && (
                <Chip
                  label={`CRM - ${knockName}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab
              value="internalSettings"
              icon={<SettingsIcon />}
              iconPosition="start"
              label="Settings"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              value="pms"
              icon={<StorageIcon />}
              iconPosition="start"
              label="PMS Setup"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              value="communities"
              icon={<ApartmentIcon />}
              iconPosition="start"
              label="Onboarding"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              value="integrations"
              icon={<RocketLaunchIcon />}
              iconPosition="start"
              label="Go Live"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              value="featureFlags"
              icon={<TuneIcon />}
              iconPosition="start"
              label="Feature Flags"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>

          {isTenantLoaded ? (
            <>
              {activeTab === 'internalSettings' && (
                <Settings
                  tenantId={tenantId}
                  restrictCommunication={restrictCommunication}
                  isGPTEnabled={isGPTEnabled}
                />
              )}
              {activeTab === 'communities' && (
                <Communities tenantId={tenantId} crmSettings={crmSettings} />
              )}
              {activeTab === 'pms' && (
                <PMS
                  info={info}
                  infoPopUPHandler={infoPopUPHandler}
                  tenantId={tenantId}
                />
              )}
              {activeTab === 'integrations' && (
                <Integrations adminTenantId={tenantId} />
              )}
              {activeTab === 'featureFlags' && (
                <FeatureFlags tenantId={tenantId} />
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      )}
      <ClientInfo
        setNotification={setNotification}
        notification={notification}
        info={info}
        infoPopUPHandler={infoPopUPHandler}
      />
    </Layout>
  );
};
export default SelfServe;
